import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Search from '../../../../assets/icons/General/search.svg?react';
import Input from '../../../widgets/Input';
import Checkbox from '../../../widgets/Checkbox';
import Chevron from '../../../../assets/icons/General/chevron.svg?react';
import Close from '../../../../assets/icons/General/close.svg?react';

interface Option {
  value: string;
  label: string;
  id?: any;
}

interface IMultiSelectProps {
  options: Option[];
  placeholder: string;
  selectedItems: { value: string; id: any }[];
  setSelectedItems: React.Dispatch<
    React.SetStateAction<{ value: string; id: any }[]>
  >;
}

const OrgSelection: React.FC<IMultiSelectProps> = ({
  options,
  placeholder,
  selectedItems,
  setSelectedItems,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [selectMatchingChecked, setSelectMathcingChecked] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();
  const maxDisplayItems = 3;

  const toggleDropdown = () => setIsOpen(!isOpen);
  const closeDropdown = () => setIsOpen(false);

  const handleMatchingAllToggle = () => {
    if (selectMatchingChecked) {
      setSelectedItems([]);
    } else {
      setSelectedItems(
        filteredOptions.map((option) => ({
          value: option.value,
          id: option.id,
        })),
      );
    }
    setSelectMathcingChecked(!selectMatchingChecked);
  };

  const handleSelectAllToggle = () => {
    if (selectAllChecked) {
      setSelectedItems([]);
    } else {
      setSelectedItems(
        options.map((option) => ({
          value: option.value,
          id: option.id,
        })),
      );
    }

    setSelectAllChecked(!selectAllChecked);
  };

  const handleSelectItem = (value: string, id: any) => {
    if (selectedItems.some((item) => item.value === value)) {
      setSelectedItems(selectedItems.filter((item) => item.value !== value));
    } else {
      setSelectedItems([...selectedItems, { value, id }]);
    }
  };

  const handleRemoveItem = (id: any) => {
    setSelectedItems(selectedItems.filter((item) => item.id !== id));
  };

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  useEffect(() => {
    if (selectedItems.length == options.length) setSelectAllChecked(true);
    else setSelectAllChecked(false);
  }, [selectedItems]);

  useEffect(() => {
    if (searchTerm) {
      if (selectedItems.length === filteredOptions.length) {
        setSelectMathcingChecked(true);
      } else {
        setSelectMathcingChecked(false);
      }
    }
  }, [searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className={`border-black-300 flex min-h-[42.67px] cursor-pointer items-center overflow-hidden rounded-lg border pl-4 transition-all duration-75 ${
          isOpen ? 'shadow-[0px_0px_0px_4px_#1018280D]' : 'border-black-300'
        }`}
        onClick={toggleDropdown}
      >
        <Search className="mr-2 h-5 min-w-[20px]" />
        <div className="mr-auto flex flex-nowrap gap-x-[6px] py-2">
          {/*{selectedItems.slice(0, maxDisplayItems).map((item) => (*/}
          {selectedItems.map((item) => (
            <div
              key={item.id}
              className="border-black-300 flex min-w-fit items-center overflow-hidden rounded-md border"
            >
              <span className="text-black-700 py-[2px] pr-[3px] pl-[9px] text-sm font-medium select-none">
                {item.value}
              </span>
              <div
                className="hover:bg-black-100 flex h-full cursor-pointer items-center px-2 transition-all duration-75"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveItem(item.id);
                }}
              >
                <Close stroke="var(--color-black-400)" className="h-2 w-2" />
              </div>
            </div>
          ))}

          {!selectedItems.length && (
            <span className="text-black-400">{placeholder}</span>
          )}
        </div>
        <div
          className={`absolute right-[1px] z-10 flex h-10 min-w-max content-center items-center gap-x-3 rounded-r-lg bg-white pr-5 pl-2 ${
            selectedItems.length > maxDisplayItems && 'border-l pl-1'
          }`}
        >
          {selectedItems.length > maxDisplayItems && (
            <div className="min-w-[64px]">
              <span className="text-black-500 text-sm">
                +{selectedItems.length - maxDisplayItems} more
              </span>
            </div>
          )}

          <div className="">
            <Chevron
              className={`transform ${isOpen ? 'rotate-180' : ''}`}
              stroke="var(--color-black-600)"
            />
          </div>
        </div>
      </div>
      {isOpen && (
        <div className="absolute z-10 mt-2 max-h-[396px] w-full overflow-auto rounded-lg border bg-white shadow-[0px_12px_16px_-4px_#10182814]">
          <div className="">
            <p className="border-black-200 sticky top-0 z-[9] border-b bg-white px-[6px] py-[2px]">
              <Input
                placeholder={t('SEARCH') + '...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="mt-1 mb-2"
                inputClass={`focus:shadow-[0px_0px_0px_4px_#1018280D] focus:border-inherit`}
              />
              {!searchTerm ? (
                <div onClick={() => handleSelectAllToggle()}>
                  <Checkbox
                    checked={selectAllChecked}
                    name={`-text-filter`}
                    onClick={(e: any) => e.stopPropagation()}
                    label={t('filterSelectAll')}
                    parentClass=" !px-[10px]"
                  />
                </div>
              ) : (
                filteredOptions.length > 0 && (
                  <div onClick={() => handleMatchingAllToggle()}>
                    <Checkbox
                      checked={selectMatchingChecked}
                      name={`-text-filter2`}
                      onClick={(e: any) => e.stopPropagation()}
                      label={t('SELECT_ALL_MATCHING')}
                      parentClass=" !px-[10px]"
                    />
                  </div>
                )
              )}
              {filteredOptions.length === 0 && (
                <div className="mt-[9px] py-[11px] pl-4 text-sm text-[#636C73]">
                  No results found.
                </div>
              )}
            </p>
            {filteredOptions.map((option) => (
              <div
                key={option.value}
                className="flex cursor-pointer items-center px-[6px] py-[2px]"
                onClick={() => handleSelectItem(option.value, option.id)}
              >
                <Checkbox
                  label={option.label}
                  checked={selectedItems.some(
                    (item) => item.value === option.value,
                  )}
                  name={option.label}
                  parentClass="!px-[10px] w-full"
                  labelClass=" !font-medium"
                  // inputClass="!hidden"
                  onClick={(e: any) => e.stopPropagation()}
                />
                {/*{selectedItems.includes(option.value) && (*/}
                {/*  <span className="ml-2 text-xs text-green-500">✔</span> // Checkmark to indicate selected item*/}
                {/*)}*/}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
export default OrgSelection;
